import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { CancelSaleReason, DealerInfo } from '@app-services/oem-dealer.service';
import { Vehicle } from '@app-model/vehicle';
import { MatRadioModule } from '@angular/material/radio';

export interface DialogReasonCancellationOption {
  value: string;
  label: string;
}

export interface DialogReasonCancellationData {
  vehicle: Vehicle;
  currentBidderInfo?: DealerInfo;
  isAdmin: boolean;
  canCancelSell: boolean;
  title?: string;
  description?: string;
  closeLabel?: string;
  reasons?: DialogReasonCancellationOption[];
}

export interface DialogReasonCancellationResult {
  cancellationReason: string;
  cancellationNote?: string;
  addDealerBlacklist: boolean;
}

@Component({
  selector: 'app-dialog-reason-cancellation',
  standalone: true,
  imports: [MatSlideToggleModule, MatRadioModule, FormsModule],
  templateUrl: './dialog-reason-cancellation.component.html',
  styleUrl: './dialog-reason-cancellation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogReasonCancellationComponent {

  private readonly dialogRef = inject(MatDialogRef<DialogReasonCancellationComponent>);
  readonly data: DialogReasonCancellationData = inject(MAT_DIALOG_DATA);

  readonly reasons = this.data.reasons ?? Object.entries(CancelSaleReason).map(([value, label]) => ({ value, label }));
  readonly maxOtherReasonLength = 150;
  readonly otherReasonKey = 'OTHER';
  readonly title = this.data.title ?? `Deseja cancelar a ${this.data.vehicle.sold && this.data.isAdmin ? 'venda' : 'negociação'}?`;
  readonly description = this.data.description;
  readonly closeLabel = this.data.closeLabel ?? (this.data.vehicle.sold && this.data.canCancelSell ? 'Cancelar' : 'Manter negociação ativa');

  reason = signal<string | null>(null);
  otherReasonDetail = signal('');
  addDealerBlacklist = signal<boolean>(false);

  isOtherReason = computed(() => this.reason() === this.otherReasonKey);

  readonly showRefundNotice = computed(
    () =>
      this.data.vehicle.sold === true
      && this.data.canCancelSell
      && this.data.vehicle.comissionPaid === true,
  );

  readonly refundBuyerName = computed(() => this.data.currentBidderInfo?.name?.trim() ?? '');

  canConfirm = computed(() => {
    const selected = this.reason();
    if (!selected) {
      return false;
    }
    if (this.isOtherReason()) {
      return this.otherReasonDetail().trim().length > 5;
    }
    return true;
  });

  onReasonChange(value: string): void {
    this.reason.set(value);
    if (value !== this.otherReasonKey) {
      this.otherReasonDetail.set('');
    }
  }

  onOtherReasonDetailChange(value: string): void {
    this.otherReasonDetail.set(value.slice(0, this.maxOtherReasonLength));
  }

  confirm(): void {
    if (!this.canConfirm()) {
      return;
    }

    this.dialogRef.close({
      cancellationReason: this.reason()!,
      cancellationNote: this.isOtherReason() ? this.otherReasonDetail().trim() : undefined,
      addDealerBlacklist: this.addDealerBlacklist(),
    } satisfies DialogReasonCancellationResult);
  }

  close(): void {
    this.dialogRef.close(null);
  }
}
